/**
 * Pure operations over the table field's string[][] matrix (Fields\Table).
 * Legacy data may store rows as objects — normalize on read, always write
 * arrays back (same contract AdminTable.vue applies). All ops return fresh
 * arrays; row/column removal refuses to empty the matrix.
 */

export type TableMatrix = string[][];

export function normalizeMatrix(raw: unknown): TableMatrix {
    if (!Array.isArray(raw)) {
        return [];
    }

    const rows = raw.map((row): string[] => {
        const cells = Array.isArray(row) ? row : typeof row === 'object' && row !== null ? Object.values(row) : [];

        return cells.map((cell) => (typeof cell === 'string' ? cell : cell == null ? '' : String(cell)));
    });

    const width = Math.max(0, ...rows.map((row) => row.length));

    return rows.map((row) => [...row, ...Array<string>(width - row.length).fill('')]);
}

export function setCell(matrix: TableMatrix, row: number, col: number, text: string): TableMatrix {
    if (row < 0 || row >= matrix.length || col < 0 || col >= (matrix[row]?.length ?? 0)) {
        return matrix;
    }

    return matrix.map((cells, index) => (index === row ? cells.map((cell, c) => (c === col ? text : cell)) : [...cells]));
}

export function insertRow(matrix: TableMatrix, index: number): TableMatrix {
    const width = Math.max(1, ...matrix.map((row) => row.length));
    const at = Math.max(0, Math.min(matrix.length, index));
    const next = matrix.map((row) => [...row]);

    next.splice(at, 0, Array<string>(width).fill(''));

    return next;
}

export function removeRow(matrix: TableMatrix, index: number): TableMatrix {
    if (matrix.length <= 1 || index < 0 || index >= matrix.length) {
        return matrix;
    }

    return matrix.filter((_, row) => row !== index).map((row) => [...row]);
}

export function insertColumn(matrix: TableMatrix, index: number): TableMatrix {
    return matrix.map((row) => {
        const at = Math.max(0, Math.min(row.length, index));
        const next = [...row];

        next.splice(at, 0, '');

        return next;
    });
}

export function removeColumn(matrix: TableMatrix, index: number): TableMatrix {
    const width = Math.max(0, ...matrix.map((row) => row.length));

    if (width <= 1 || index < 0 || index >= width) {
        return matrix;
    }

    return matrix.map((row) => row.filter((_, col) => col !== index));
}
